'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { leaders } from '@/app/data/leaders';

interface ReturnMetric {
  id: number;
  metric: string;
  target: string;
  timeframe: string;
  description: string;
}

interface ReturnsPhilosophyProps {
  className?: string;
}

const ReturnsPhilosophy = ({ className = '' }: ReturnsPhilosophyProps) => {
  const returnMetrics: ReturnMetric[] = [
    {
      id: 1,
      metric: 'Internal Rate of Return (IRR)',
      target: '15-20%',
      timeframe: '10-15 years',
      description:
        'Net returns after fees, reflecting patient capital approach with compounding value creation through systematic infrastructure development.',
    },
    {
      id: 2,
      metric: 'Cash-on-Cash Multiple',
      target: '2.5-3.5x',
      timeframe: 'Full investment cycle',
      description:
        'Total capital returned to investors, demonstrating sustainable value creation through operational excellence and strategic exits.',
    },
    {
      id: 3,
      metric: 'Development Impact ROI',
      target: '5-8x',
      timeframe: 'Economic multiplier effect',
      description:
        'Quantified economic value generated per dollar invested, measured through job creation, GDP contribution, and infrastructure utilization.',
    },
    {
      id: 4,
      metric: 'Portfolio Diversification',
      target: '6-8 sectors',
      timeframe: 'Phase 1-2 deployment',
      description:
        'Risk-adjusted sector allocation balancing infrastructure, agriculture, manufacturing, and services for resilient portfolio performance.',
    },
  ];

  const philosophyPrinciples = [
    {
      id: 1,
      title: 'Patient Capital Discipline',
      description:
        'We reject short-term extraction in favor of long-term value creation, aligning investment horizons with infrastructure development timelines and economic transformation cycles.',
      icon: 'ClockIcon',
    },
    {
      id: 2,
      title: 'Blended Value Creation',
      description:
        'Financial returns and development impact are not trade-offs but complementary outcomes of systematic, sustainable investment in high-potential sectors.',
      icon: 'ScaleIcon',
    },
    {
      id: 3,
      title: 'Risk-Adjusted Optimization',
      description:
        'We pursue attractive returns through sophisticated risk management, not excessive risk-taking, ensuring institutional-grade protection with development finance innovation.',
      icon: 'ShieldCheckIcon',
    },
    {
      id: 4,
      title: 'Transparent Performance Reporting',
      description:
        'Quarterly reporting on financial metrics, impact outcomes, and risk factors provides investors with comprehensive visibility into portfolio performance and development progress.',
      icon: 'DocumentChartBarIcon',
    },
  ];

const [leaderIndex, setLeaderIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setLeaderIndex((prev) => (prev + 1) % leaders.length);
    }, 5000); // rotate every 5s
    return () => clearInterval(timer);
  }, [])
  return (
    <section data-header="light" className={`py-4 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-8  items-center mb-2 sm:mb-4 md:mb-6">
          <div>
            <h2 className="font-headline text-3xl lg:text-5xl font-semibold text-foreground mb-2 sm:mb-4 md:mb-6">
              <span className="text-accent">Returns Philosophy</span>: Disciplined Value Creation
            </h2>
            <p className="font-body text-base sm:text-lg text-text-secondary leading-relaxed mb-4 md:mb-6">
              Our approach to returns reflects institutional sophistication combined with
              development finance expertise. We target attractive risk-adjusted returns through
              systematic methodology, not speculative positioning.
            </p>
            <div className="space-y-6">
              {philosophyPrinciples.map((principle) => (
                <div key={principle.id} className="flex items-start space-x-2 sm:space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon
                      name={principle.icon as any}
                      size={20}
                      variant="outline"
                      className="text-accent"
                    />
                  </div>
                  <div>
                    <h3 className="font-body text-base font-semibold text-foreground mb-2">
                      {principle.title}
                    </h3>
                    <p className="font-body text-sm text-text-secondary leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-institutional">
              <AppImage
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?w=800&q=80"
                alt="Business professionals analyzing financial charts and investment data on large monitor in modern office"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-lg shadow-institutional max-w-xs">
              <p className="font-body text-sm leading-relaxed animate-quote-in">
                “{leaders[leaderIndex].quote}”
              </p>
              <p className="font-body text-xs text-gray-300 mt-3">
                — {leaders[leaderIndex].name}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg p-2 lg:p-12">
          <h3 className="font-headline text-2xl lg:text-3xl font-semibold text-foreground mb-2 sm:mb-4 md:mb-6 ">
            Target Return Metrics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
            {returnMetrics.map((metric) => (
              <div
                key={metric.id}
                className="bg-card border border-border rounded-lg p-3 sm:p-6 hover:shadow-institutional transition-shadow duration-300"
              >
                <div className="flex items-start justify-between mb-2 sm:mb-4">
                  <h4 className="font-body text-sm sm:text-base font-semibold text-foreground">
                    {metric.metric}
                  </h4>
                  <span className="font-headline text-xl sm:text-2xl font-semibold text-accent">
                    {metric.target}
                  </span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 mb-2 sm:mb-4">
                  <Icon
                    name="ClockIcon"
                    size={16}
                    variant="outline"
                    className="text-muted-foreground"
                  />
                  <span className="font-body text-sm text-muted-foreground">
                    {metric.timeframe}
                  </span>
                </div>
                <p className="font-body text-sm text-text-secondary leading-relaxed">
                  {metric.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReturnsPhilosophy;
